/**
 * What the network is doing: the fetch queue and each adapter's throughput.
 *
 * Its own tab, beside Settings, Site Parsers and Usage. On the Settings page it
 * sat under every fetcher's setup, so watching the queue meant scrolling past
 * the whole form - and it is something you watch, not something you set.
 */
import { useEffect, useState } from 'react';
import api from '../services/api';
import FetchQueuePanel from '../components/network/FetchQueuePanel';
import ThroughputPanel from '../components/network/ThroughputPanel';

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        {hint && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{hint}</p>}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

export default function NetworkActivity() {
  // The adapters' display names, for the queue's rows.
  const [labels, setLabels] = useState<Record<string, string>>({});
  useEffect(() => {
    api
      .getNetworkAdapters()
      .then((a) => setLabels(Object.fromEntries((a?.adapters ?? []).map((x) => [x.adapter, x.label]))))
      .catch(() => setLabels({}));
  }, []);
  return (
    <div className="space-y-6" data-testid="network-activity">
      <Section title="Queue" hint="What is waiting, what each adapter is working on, and what failed.">
        <FetchQueuePanel labels={labels} />
      </Section>
      <Section
        title="Throughput"
        hint="Per adapter: fetches, how many worked, time each, how full it ran, and how long requests waited for it. An adapter that runs near full while requests wait long is the one to give more room."
      >
        <ThroughputPanel />
      </Section>
    </div>
  );
}
